import { CartLinesUpdateEvent } from '@shopify/events';

const requestLimit = 8000;
const operationLimit = 15000;
const recoveryLimit = 4000;
const uncertainProducts = new Set();
const storagePrefix = 'bv-packie-unconfirmed:v1:';
let pending = false;

function hasUnconfirmedAdd(productId) {
  if (uncertainProducts.has(productId)) return true;
  try {
    if (sessionStorage.getItem(storagePrefix + productId)) {
      uncertainProducts.add(productId);
      return true;
    }
  } catch {}
  return false;
}

function markUnconfirmedAdd(productId, value) {
  if (value) uncertainProducts.add(productId);
  else uncertainProducts.delete(productId);
  try {
    if (value) sessionStorage.setItem(storagePrefix + productId, '1');
    else sessionStorage.removeItem(storagePrefix + productId);
  } catch {}
}

function showRecovery(offer, uncertain = false) {
  const message = offer.querySelector('[data-packie-error]');
  const text = uncertain ? offer.dataset.uncertain : offer.dataset.error;
  if (message) {
    if (message.textContent !== text) message.textContent = text;
    message.hidden = false;
  }
  const link = offer.querySelector('[data-packie-recovery]');
  if (link) link.hidden = false;
}

function enableOffers(root = document) {
  root.querySelectorAll('[data-packie-offer]').forEach((offer) => {
    const button = offer.querySelector('[data-packie-add]');
    if (!button) return;
    const uncertain = hasUnconfirmedAdd(offer.dataset.productId);
    button.hidden = false;
    button.disabled = pending;
    const label = pending ? offer.dataset.busyLabel : uncertain ? offer.dataset.checkLabel : offer.dataset.addLabel;
    if (button.textContent !== label) button.textContent = label;
    if (uncertain && !pending) showRecovery(offer, true);
  });
}

if (!customElements.get('bv-packie-slot')) {
  customElements.define('bv-packie-slot', class extends HTMLElement {
    connectedCallback() {
      enableOffers(this);
      this.observer = new MutationObserver(() => enableOffers(this));
      this.observer.observe(this, { childList: true, subtree: true });
    }
    disconnectedCallback() {
      this.observer?.disconnect();
    }
  });
}
enableOffers();

async function requestJson(url, options = {}, deadline = performance.now() + requestLimit) {
  const remaining = Math.min(requestLimit, deadline - performance.now());
  if (remaining <= 0) throw new Error('Cart request timed out');
  const controller = new AbortController();
  let timer;
  try {
    return await Promise.race([
      (async () => {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          signal: controller.signal,
        });
        if (!response.ok) {
          const error = new Error('Cart request failed');
          error.status = response.status;
          throw error;
        }
        return response.json();
      })(),
      new Promise((_, reject) => {
        timer = setTimeout(() => {
          controller.abort();
          reject(new Error('Cart request timed out'));
        }, remaining);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function readCart(url, deadline) {
  const cart = await requestJson(`${url}.js`, { headers: { Accept: 'application/json' } }, deadline);
  if (!Array.isArray(cart?.items) || typeof cart.item_count !== 'number') throw new Error('Invalid cart');
  return cart;
}

function completeSections(sections, ids) {
  return ids.length > 0 && ids.every((id) => typeof sections?.[id] === 'string' && sections[id].trim());
}

async function readSections(ids, deadline) {
  if (!ids.length) throw new Error('Cart sections unavailable');
  const url = new URL(window.location.href);
  url.searchParams.delete('section_id');
  url.searchParams.set('sections', ids.join(','));
  const sections = await requestJson(url, { headers: { Accept: 'application/json' } }, deadline);
  if (!completeSections(sections, ids)) throw new Error('Incomplete cart sections');
  return sections;
}

function notifyCart(cart, sections, variantId, didAdd) {
  document.dispatchEvent(new CartLinesUpdateEvent({
    action: didAdd ? 'add' : 'change',
    context: 'cart',
    lines: didAdd ? [{ merchandiseId: variantId, quantity: 1 }] : [],
    promise: Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, itemCount: cart.item_count, sections, source: 'bv-packie', didError: false },
    }),
  }));
}

document.addEventListener('click', async (event) => {
  const button = event.target.closest?.('[data-packie-add]');
  if (!button) return;
  event.preventDefault();
  if (pending) return;
  const offer = button.closest('[data-packie-offer]');
  if (!offer) return;

  pending = true;
  enableOffers();
  offer.setAttribute('aria-busy', 'true');
  const message = offer.querySelector('[data-packie-error]');
  const recoveryLink = offer.querySelector('[data-packie-recovery]');
  if (message) message.hidden = true;
  if (recoveryLink) recoveryLink.hidden = true;

  const ids = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const checkoutButtons = [...document.querySelectorAll('button[name="checkout"]')].filter((item) => !item.disabled);
  checkoutButtons.forEach((item) => { item.disabled = true; });
  const unlockCheckout = () => {
    checkoutButtons.forEach((item) => { if (item.isConnected) item.disabled = false; });
  };

  const productId = offer.dataset.productId;
  const variantId = offer.dataset.variantId;
  const containsBooster = (cart) => cart.items.some((item) => String(item.product_id) === productId);
  const previouslyUnconfirmed = hasUnconfirmedAdd(productId);
  const deadline = performance.now() + operationLimit;
  let attempted = false;
  let succeeded = false;
  let rejected = false;

  try {
    let cart = await readCart(offer.dataset.cartUrl, deadline);
    let sections;

    if (!containsBooster(cart)) {
      if (previouslyUnconfirmed) throw new Error('Previous add not confirmed');
      if (!cart.item_count) throw new Error('Empty cart');
      attempted = true;
      markUnconfirmedAdd(productId, true);
      let result;
      try {
        result = await requestJson(`${offer.dataset.addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections: ids.join(','),
            sections_url: window.location.pathname,
          }),
        }, deadline);
        if (result?.status) {
          const error = new Error('Booster unavailable');
          error.status = Number(result.status);
          throw error;
        }
      } catch (error) {
        if ([400, 404, 422].includes(error.status)) {
          rejected = true;
          markUnconfirmedAdd(productId, false);
        }
        throw error;
      }
      sections = result?.sections;
      cart = await readCart(offer.dataset.cartUrl, deadline);
      if (!containsBooster(cart)) throw new Error('Add not confirmed');
    }

    if (!completeSections(sections, ids)) sections = await readSections(ids, deadline);
    notifyCart(cart, sections, variantId, attempted || previouslyUnconfirmed);
    markUnconfirmedAdd(productId, false);
    succeeded = true;
  } catch {
    unlockCheckout();
    if (attempted && !rejected) {
      try {
        const recoveryDeadline = performance.now() + recoveryLimit;
        const cart = await readCart(offer.dataset.cartUrl, recoveryDeadline);
        if (containsBooster(cart)) {
          const sections = await readSections(ids, recoveryDeadline);
          notifyCart(cart, sections, variantId, true);
          markUnconfirmedAdd(productId, false);
          succeeded = true;
        }
      } catch {}
    }
    if (!succeeded) showRecovery(offer, hasUnconfirmedAdd(productId));
  } finally {
    if (succeeded) {
      document.querySelectorAll('[data-packie-offer]').forEach((item) => {
        if (item.dataset.productId === productId) item.hidden = true;
      });
      const container = offer.closest('cart-items-component');
      requestAnimationFrame(() => requestAnimationFrame(() =>
        container?.querySelector('button[name="checkout"]')?.focus({ preventScroll: true })));
    }
    pending = false;
    unlockCheckout();
    offer.removeAttribute('aria-busy');
    enableOffers();
  }
}, true);
